<template>
    <span class="sp-apikey" @click="generateAndShow">APIKey</span>

    <Transition name="sp-modal">
        <div
            v-if="visible"
            ref="apiKeyDialog"
            tabindex="0"
            class="sp-modal-mask"
            @keydown.esc="closeDialog"
            @click.self="closeDialog"
        >
            <div class="sp-modal-wrapper">
                <div class="sp-modal-container sp-apikey-modal">
                    <div class="sp-modal-header">
                        <span class="sp-modal-header-title">API Key</span>
                        <span
                            tabindex="0"
                            class="sp-modal-header-closebtn material-symbols-outlined"
                            @click="closeDialog"
                        >close</span>
                    </div>
                    <div class="sp-modal-content">
                        <div v-if="loading" class="sp-apikey-status">Generating new API key...</div>
                        <div v-else-if="error" class="sp-apikey-status sp-apikey-status--error">{{ error }}</div>
                        <template v-else>
                            <p class="sp-apikey-warning">
                                Copy this key now. It will not be shown again.
                                Any previous API key for your account has been invalidated.
                            </p>
                            <p class="sp-apikey-hint">
                                Use in the <code>Authorization</code> header:
                                <code>Token &lt;key&gt;</code>
                            </p>
                            <div class="sp-apikey-block">
                                <code class="sp-apikey-value">{{ apiKey }}</code>
                                <button
                                    type="button"
                                    class="sp-apikey-copy"
                                    :title="copied ? 'Copied' : 'Copy'"
                                    @click="copyKey"
                                >
                                    <span class="material-symbols-outlined">{{ copied ? 'check' : 'content_copy' }}</span>
                                </button>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </Transition>
</template>

<script>
export default {
    name: 'ApiKeyButton',
    data() {
        return {
            visible: false,
            loading: false,
            error: null,
            apiKey: '',
            copied: false,
        }
    },
    methods: {
        async generateAndShow() {
            this.visible = true
            this.loading = true
            this.error = null
            this.apiKey = ''
            this.copied = false

            await this.$axios.post('/auth/token/')
                .then((response) => {
                    this.apiKey = response.data.detail.key
                })
                .catch(({ response }) => {
                    this.error = response?.data?.detail || 'Unable to generate API key'
                })
                .finally(() => {
                    this.loading = false
                    this.$nextTick(() => this.$refs.apiKeyDialog?.focus())
                })
        },
        closeDialog() {
            this.visible = false
            this.apiKey = ''
            this.copied = false
        },
        async copyKey() {
            if (!this.apiKey) {
                return
            }
            try {
                await navigator.clipboard.writeText(this.apiKey)
                this.copied = true
                setTimeout(() => { this.copied = false }, 2000)
            } catch {
                const textarea = document.createElement('textarea')
                textarea.value = this.apiKey
                textarea.style.position = 'fixed'
                textarea.style.opacity = '0'
                document.body.appendChild(textarea)
                textarea.select()
                document.execCommand('copy')
                document.body.removeChild(textarea)
                this.copied = true
                setTimeout(() => { this.copied = false }, 2000)
            }
        },
    },
}
</script>

<style scoped>
.sp-apikey {
    font-size: 14px;
    color: #fff;
    margin-right: 16px;
    cursor: pointer;
    user-select: none;
    font-family: Open Sans, Roboto, sans-serif;
}
.sp-apikey:hover {
    text-decoration: underline;
}
.sp-apikey-modal {
    width: 520px;
}
.sp-apikey-warning {
    margin: 0 0 12px;
    font-size: 13px;
    line-height: 1.4;
    color: var(--color-severity-high);
}
.sp-apikey-hint {
    margin: 0 0 12px;
    font-size: 13px;
    line-height: 1.4;
}
.sp-apikey-hint code {
    font-size: 12px;
    background: var(--vs-search-input-bg);
    padding: 1px 4px;
    border-radius: 3px;
}
.sp-apikey-block {
    display: flex;
    align-items: stretch;
    gap: 8px;
    border: 1px solid var(--vs-border-color);
    border-radius: 5px;
    background: var(--vs-search-input-bg);
    padding: 8px 8px 8px 12px;
}
.sp-apikey-value {
    flex: 1;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 13px;
    word-break: break-all;
    white-space: pre-wrap;
    color: var(--color-fg);
}
.sp-apikey-copy {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: var(--color-fg);
    cursor: pointer;
}
.sp-apikey-copy:hover {
    background: var(--color-bg-header);
}
.sp-apikey-copy .material-symbols-outlined {
    font-size: 20px;
}
.sp-apikey-status {
    font-size: 13px;
    color: var(--color-fg);
}
.sp-apikey-status--error {
    color: var(--color-severity-high);
}
.sp-modal-mask {
    position: fixed;
    z-index: 9999;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: table;
    transition: opacity 0.3s ease;
}
.sp-modal-wrapper {
    display: table-cell;
    vertical-align: middle;
}
.sp-modal-container {
    margin: 0 auto;
    border: 1px solid var(--color-border-header);
    border-radius: 2px;
    background-color: var(--color-bg);
    color: var(--color-fg);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
    transition: all 0.3s ease;
    font-family: 'Source Sans Pro', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 14px;
}
.sp-modal-header {
    height: 40px;
    user-select: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-left: 10px;
    padding-right: 10px;
    font-weight: bold;
    color: var(--color-fg-header);
    background-color: var(--color-bg-header);
    border-top: 1px solid var(--color-border-header);
    border-bottom: 1px solid var(--color-border-header);
}
.sp-modal-header-closebtn {
    cursor: pointer;
}
.sp-modal-content {
    margin: 20px;
}
.sp-modal-enter-from,
.sp-modal-leave-to {
    opacity: 0;
}
</style>
